// Component wrapper for kendo dropdownlist that provides a few default props
import * as React from "react";
import {
  DropDownList,
  DropDownListBlurEvent,
  DropDownListChangeEvent,
  DropDownListProps,
} from "@progress/kendo-react-dropdowns";
import { StackLayout } from "@progress/kendo-react-layout";
import { CPText } from "shared/components/base";
import { ValueObject } from "shared/types";
import { getFieldError } from "shared/helpers";

interface CPDropDownListProps extends Omit<
  DropDownListProps,
  "onChange" | "name" | "onBlur"
> {
  /** Key in each data item whose value is emitted in the change/blur event instead of the whole object. If omitted, the entire selected object is emitted. */
  valueKey?: string;
  /** Marks the field as required. */
  required?: boolean;
  /** Helper text shown below the dropdown when there is no error. */
  helpText?: string;
  /** Error text shown below the dropdown in red. */
  errorMsg?: string;
  /** Raw API error response `{ errors: [{ propertyName, errorMessage }] }`. Used with `name` to resolve a field-level error. */
  error?: any;
  /** Unique field name passed through in all normalised events. */
  name: string;
  /** Callback fired when the selected value changes. Receives a normalised `CPDropDownListEventChangeEvent`. */
  onChange?: (event: CPDropDownListEventChangeEvent) => void;
  /** Callback fired when the dropdown loses focus. Receives a normalised `CPDropDownListEvent`. */
  onBlur?: (event: CPDropDownListEvent) => void;
}

export interface CPDropDownListEventChangeEvent extends Omit<
  CPDropDownListEvent,
  "type"
> {
  type: "CPDropDownListEventChangeEvent";
}

export interface CPDropDownListEvent {
  name: string;
  type: "CPDropDownListEvent";
  syntheicEvent: React.SyntheticEvent<HTMLElement, Event>;
  value: string | ValueObject;
}

/**
 * Wrapper around the Kendo `DropDownList` with normalised change/blur events, optional `valueKey`
 * extraction, floating label, and inline help/error text.
 *
 * @example
 * ```tsx
 * // Simple static list
 * <CPDropDownList
 *   name="status"
 *   label="Status"
 *   data={[{ text: 'Active', value: 1 }, { text: 'Inactive', value: 0 }]}
 *   textField="text"
 *   dataItemKey="value"
 *   valueKey="value"
 *   value={form.status}
 *   onChange={handleChange}
 * />
 *
 * // With error and required indicator
 * <CPDropDownList
 *   name="role"
 *   label="Role *"
 *   required
 *   data={roles}
 *   textField="name"
 *   dataItemKey="id"
 *   valueKey="id"
 *   value={form.roleId}
 *   errorMsg={errors.role}
 *   onChange={handleChange}
 * />
 * ```
 */
const CPDropDownList: React.FC<CPDropDownListProps> = (props) => {
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (props.error != null) setDirty(false);
  }, [props.error]);

  let value = props.value ?? null;

  if (value && props.data && props.valueKey) {
    value = props.data.find(
      (x: ValueObject) => x[props.valueKey ?? ""] === value,
    );
  }

  const handleChange = (event: DropDownListChangeEvent) => {
    setDirty(true);
    if (props.onChange) {
      props.onChange({
        name: props.name,
        type: "CPDropDownListEventChangeEvent",
        syntheicEvent: event.syntheticEvent,
        value:
          props.valueKey && event.value
            ? event.value[props.valueKey]
            : event.value,
      });
    }
  };

  const handleBlur = (event: DropDownListBlurEvent) => {
    if (props.onBlur) {
      props.onBlur({
        name: props.name,
        type: "CPDropDownListEvent",
        syntheicEvent: event.syntheticEvent,
        value: props.value,
      });
    }
  };

  const resolvedError =
    props.errorMsg ??
    getFieldError(dirty ? undefined : props.error, props.name);

  return (
    <StackLayout orientation="vertical">
      <DropDownList
        {...props}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        label={props.required ? `${props.label} *` : props.label}
        size={props.size ?? "small"}
      />
      <CPText textStyle="caption" color={resolvedError ? "error" : "primary"}>
        {resolvedError ?? props.helpText}
      </CPText>
    </StackLayout>
  );
};

export default CPDropDownList;
